import Vue from "vue";
import Vuex, { Store, StoreOptions } from "vuex";
import _ from "lodash";
import assert from "assert";
import axios, { AxiosRequestConfig } from "axios";
import router from "@/router";
import { Admin, Election, State } from "@/types.ts";
import { formatTime } from "@/main";
import { reactive } from "@vue/composition-api";

Vue.use(Vuex);

function arrayBufferToBase64(buffer: ArrayBuffer) {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

async function hash(password: string) {
  if (crypto.subtle === undefined) {
    console.warn(
      "crypto unavailable in insecure context, using plain password"
    );
    return password;
  } else {
    const message = `i-pericles-client${password}`;
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);
    const b64 = arrayBufferToBase64(hashBuffer).replace(/=*$/, "");
    return b64;
  }
}

import {
  api as voteBaseURL,
  credentials as credentialsBaseURL,
  front,
} from "@/config";

const voteApiInstance = axios.create({
  baseURL: voteBaseURL,
});

const credentialsApiInstance = axios.create({
  baseURL: credentialsBaseURL,
});

async function api(store: Store<State>, args: AxiosRequestConfig) {
  const token = store.state?.token;
  if (token) {
    args.headers = args.headers || {};
    args.headers["Authorization"] =
      args.headers["Authorization"] || `Bearer ${token}`;
  }
  return voteApiInstance(args);
}

async function credentialsApi(store: Store<State>, args: AxiosRequestConfig) {
  const token = store.state?.token;
  if (token) {
    args.headers = args.headers || {};
    args.headers["Authorization"] = `Bearer ${token}`;
  }
  return credentialsApiInstance(args);
}

const storage = window.localStorage;

const store: StoreOptions<State> = {
  state: {
    administrators: null,
    elections: null,
    electionsTracker: 1,
    email: null,
    token: null,
    password: null,
    version: null,
  },
  mutations: {
    addElection(state, election) {
      assert(state.elections);
      state.elections.set(election.uuid, election);
      state.electionsTracker++;
    },
    addTrustee(state, { election: e, trustee }) {
      assert(state.elections);
      trustee = _.cloneDeep(trustee);
      const originalEmail = trustee.originalEmail || trustee.email;
      trustee.originalEmail = trustee.email;
      const election = state.elections.get(e.uuid);
      assert(election);
      // FIXME: index
      const v = _(election.externalTrustees).find(
        v => v.email === originalEmail
      );
      if (v) _.assign(v, trustee);
      else if (election.externalTrustees === undefined)
        Vue.set(election, "externalTrustees", [trustee]);
      else election.externalTrustees.push(trustee);
      state.electionsTracker++;
    },
    addVoter(state, { election: e, voter }) {
      assert(state.elections);
      const election = state.elections.get(e.uuid);
      assert(election);
      // FIXME: index
      const v = _(election.voters).find(v => v.email === voter.email);
      if (v) _.assign(v, voter);
      else election.voters.push(voter);
      state.electionsTracker++;
    },
    backendVersion(state, version) {
      state.version = version;
    },
    electionDelete(state, { election }) {
      state.elections?.delete(election.uuid);
    },
    removeTrustee(state, { election: e, trustee }) {
      assert(state.elections);
      const election = state.elections.get(e.uuid);
      assert(election);
      if (election.externalTrustees)
        // FIXME: index
        _.remove(election.externalTrustees, t => t.email === trustee.email);
      state.electionsTracker++;
    },
    removeVoter(state, { election: e, voter }) {
      assert(state.elections);
      const election = state.elections.get(e.uuid);
      assert(election);
      // FIXME: index
      _.remove(election.voters, v => v.email === voter.email);
      state.electionsTracker++;
    },
    loadAdministrators(state, administrators) {
      state.administrators = administrators;
    },
    loadElectionNames(state, elections) {
      const res = state.elections || new Map();
      for (const election of elections)
        if (!res.has(election.uuid)) res.set(election.uuid, reactive(election));
      state.elections = res;
      state.electionsTracker++;
    },
    loadElections(state, elections) {
      const conv = (e: Election) => {
        if (e.start) e.start = new Date(e.start);
        if (e.end) e.end = new Date(e.end);
        if (e.tallyTime) e.tallyTime = new Date(e.tallyTime);
        _.forEach(e.externalTrustees, t => (t.originalEmail = t.email));
        _.forEach(e.voters, v => {
          if (v.time) v.time = new Date(v.time);
        });
        return reactive(e);
      };
      const res = state.elections || new Map();
      for (const election of elections) res.set(election.uuid, conv(election));
      state.elections = res;
      state.electionsTracker++;
    },
    login(state, { email, password, token }) {
      storage.setItem("email", email);
      storage.setItem("password", password);
      state.email = email;
      state.token = token;
      state.password = password;
    },
    logout(state) {
      storage.removeItem("email");
      storage.removeItem("password");
      state.email = null;
      state.password = null;
      state.token = null;
    },
    removeAdministrator(state, admin: Admin) {
      assert(state.administrators);
      _.remove(state.administrators, a => a.email === admin.email);
      state.administrators = [...state.administrators];
    },
    saveAdministrator(state, admin: Admin) {
      assert(state.administrators);
      const a = _.find(state.administrators, a => a.email === admin.email);
      if (a) _.assign(a, admin);
      else state.administrators.push(admin);
      state.administrators = [...state.administrators];
    },
    updateElection(state, patch) {
      assert(state.elections);
      const election = state.elections.get(patch.uuid);
      assert(election);
      Object.assign(election, patch);
      state.electionsTracker++;
      return election;
    },
  },
  actions: {
    async copyVoters(store, { from, to }) {
      const credentialsResp = await credentialsApi(this, {
        method: "POST",
        url: `/${from}/copy`,
        params: { to },
      });
      const keys = _.mapValues(credentialsResp.data, v => v.publicKey);
      const resp = await api(this, {
        data: keys,
        method: "POST",
        params: { from },
        url: `/elections/${to}/copy-voters`,
      });
      const voters = resp.data;
      store.commit("updateElection", { uuid: to, voters });
      return voters;
    },
    async createElection(store) {
      const resp = await api(this, {
        method: "POST",
        url: "/elections/generate-uuid",
      });
      const uuid = resp.data;
      const data: Election = {
        archived: false,
        ballots: [],
        description: "",
        name: "",
        published: false,
        questions: [],
        threshold: 0,
        trusteesSealed: false,
        uuid,
        voters: [],
      };
      const response = await api(this, {
        method: "PUT",
        url: `/elections/${uuid}`,
        data,
      });
      store.commit("addElection", response.data);
    },
    async electionDelete(store, { election }) {
      await api(this, {
        method: "DELETE",
        url: `/elections/${election.uuid}`,
      });
      store.commit("electionDelete", { election });
    },
    async exportContactList(store, { election }) {
      const boothRoute = {
        name: "Vote",
        params: { uuid: election.uuid },
      };
      const response = await credentialsApi(this, {
        method: "GET",
        url: `/${election.uuid}/export`,
        params: {
          name: election.name,
          booth: `${front}${router.resolve(boothRoute).href}`,
        },
        responseType: 'blob',
      });
      const content = new Blob([response.data], { type: 'text/csv' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(content)
      link.download = 'voters.csv'
      link.click()
      URL.revokeObjectURL(link.href)
    },
    async getCode(store, { election, fallbackPhone, voter }) {
      const params = {
        email: voter.email,
      };
      if (fallbackPhone) Object.assign(params, { phone: fallbackPhone });
      const resp = await api(this, {
        method: "POST",
        params,
        url: `/elections/${election.uuid}/voters/${voter.email}/send-code`,
      });
      return resp.data;
    },
    async loadAdministrators(store) {
      const resp = await api(this, {
        url: "/admins",
        validateStatus(status) {
          return status < 500;
        },
      });
      if (resp.status == 200) store.commit("loadAdministrators", resp.data);
    },
    async loadElectionNames(store) {
      const resp = await api(this, {
        url: "/elections",
        params: { "only-names": "true" },
      });
      store.commit("loadElectionNames", resp.data);
    },
    async loadElections(store) {
      const resp = await api(this, { url: "/elections" });
      store.commit("loadElections", resp.data);
    },
    async loadElection(store, { ballots, code, email, pub, uuid }) {
      const credentialLogs = async () => {
        if (!code) return [];
        else {
          const token = await api(this, {
            params: { code },
            url: `/elections/${uuid}/trustees/${email}/token`,
          });
          store.state.token = token.data.token; // ye ye
          const resp = await credentialsApi(this, {
            url: `/${uuid}/logs`,
          });
          return resp.data;
        }
      };
      const [election, logs] = await Promise.all([
        api(this, {
          url: `/elections/${uuid}`,
          params: { ballots, code, email, pub },
          validateStatus(status) {
            return status < 500;
          },
        }),
        credentialLogs(),
      ]);
      if (election.status == 200) {
        election.data.logs = logs.concat(election.data.logs || []);
        store.commit("loadElections", [election.data]);
      } else store.commit("loadElections", []);
    },
    async log(store, { election, line }) {
      console.log(election, line);
      await credentialsApi(this, {
        data: line,
        method: "POST",
        url: `/${election.uuid}/logs`,
      });
    },
    async removeAdministrator(store, admin: Admin) {
      await api(this, {
        method: "DELETE",
        url: `/admins/${admin.email}`,
      });
      store.commit("removeAdministrator", admin);
    },
    async saveAdministrator(store, admin: Admin) {
      admin = _.cloneDeep(admin);
      if (admin.password) admin.password = await hash(admin.password);
      await api(this, {
        method: "POST",
        url: "/admins",
        data: admin,
      });
      delete admin.password;
      store.commit("saveAdministrator", admin);
    },
    async sendEmail(store, { election, email, voter }) {
      const boothRoute = {
        name: "Vote",
        params: { uuid: election.uuid },
        query: { email: "~~EMAIL~~", pub: "~~PUBLIC_KEY~~" },
      };
      const dates = (() => {
        if (election.start && election.end)
          return `\n\nLe vote par voie électronique se déroulera entre ${formatTime(
            election.start
          )} et ${formatTime(election.end)}.`;
        else return "";
      })();
      const subject = `Votre code pour "${election.name}".`;
      const body = `Bonjour,

Vous êtes invité[e] à voter dans le cadre de ${election.name}.${dates}

L'empreinte numérique du scrutin est: ${electionFingerprint(election)}

Pour accéder à l'espace de vote, vous devez vous connecter avec votre lien
personnel :
${front}${router.resolve(boothRoute).href}

Pour valider et chiffrer votre vote, vous devrez saisir le code suivant :

~~PRIVATE_KEY~~

Vous devez le copier/coller.

Pour toutes questions techniques le jour du vote, vous pourrez contacter le
support i-Périclès (support@i-pericles.fr) jusqu'à la fermeture du scrutin.

Bien à vous,

--
i-Périclès`;
      if (voter)
        await credentialsApi(this, {
          data: body,
          headers: {
            "Content-Type": "text/plain",
          },
          method: "POST",
          params: { email, subject },
          url: `/${election.uuid}/voters/${voter.email}/send-email`,
        });
      else
        await credentialsApi(this, {
          data: body,
          headers: {
            "Content-Type": "text/plain",
          },
          method: "POST",
          params: { subject },
          url: `/${election.uuid}/send-emails`,
        });
    },
    async sendTrusteeEmail(store, { election, trustee }) {
      await api(this, {
        method: "POST",
        url: `/elections/trustees/${election.uuid}/${trustee.email}`,
      });
    },
    async updateElection(store, patch) {
      assert(store.state.elections);
      store.commit("updateElection", patch);
      const resp = await api(this, {
        method: "PUT",
        url: `/elections/${patch.uuid}`,
        data: store.state.elections.get(patch.uuid),
      });
      const election = resp.data;
      // FIXME: Not filled by that endpoint, so don't override with a
      // missing or incomplete list.
      delete election["ballots"];
      delete election["voters"];
      if (election.start) election.start = new Date(election.start);
      if (election.end) election.end = new Date(election.end);
      if (election.tallyTime) election.tallyTime = new Date(election.tallyTime);
      store.commit("updateElection", election);
      return election;
    },
    async addTrustee(store, { election, trustee, code }) {
      delete trustee.dirty;
      store.commit("addTrustee", { election, trustee: trustee });
      const data = _.cloneDeep(trustee);
      delete data.credentials?.priv;
      const resp = await api(this, {
        method: "PUT",
        url: `/elections/trustees/${election.uuid}/${trustee.originalEmail ||
          trustee.email}`,
        data,
        params: { code },
      });
      store.commit("addTrustee", { election, trustee: resp.data });
      return resp.data;
    },
    async addTrustees(store, { election, trustees }) {
      assert(store.state.elections);
      const results = _.map(trustees, async trustee =>
        this.dispatch("addTrustee", { election, trustee })
      );
      return Promise.all(results);
    },
    async setVoters(store, { election, voters }) {
      assert(store.state.elections);
      const credentialsResp = await credentialsApi(this, {
        method: "PUT",
        url: `/${election.uuid}`,
        data: _.map(voters, v => ({ email: v.email, votes: v.votes })),
      });
      const resp = await api(this, {
        method: "PUT",
        url: `/elections/${election.uuid}/voters`,
        data: {
          public_keys: credentialsResp.data, // eslint-disable-line @typescript-eslint/camelcase
          voters,
        },
      });
      const results = _.map(resp.data, async voter => {
        store.commit("addVoter", { election, voter });
      });
      return Promise.all(results);
    },
    async removeTrustee(store, { election, trustee }) {
      assert(store.state.elections);
      await api(this, {
        method: "DELETE",
        url: `/elections/trustees/${election.uuid}/${trustee.email}`,
      });
      this.commit("removeTrustee", { election, trustee });
    },
    async login(store, { email, hashedPassword, password }) {
      if (!hashedPassword) hashedPassword = await hash(password);
      const auth = btoa(`${email}:${hashedPassword}`);
      const resp = await api(this, {
        headers: {
          Authorization: `Basic ${auth}`,
        },
        method: "POST",
        url: "/login",
      });
      this.commit("login", {
        email,
        password: hashedPassword,
        token: resp.data["token"],
      });
    },
    async logout() {
      this.commit("logout");
    },
    async submitBallot(store, { ballot, code, data, election, email }) {
      const resp = await api(this, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        params: {
          code,
          data,
          email,
        },
        url: `/elections/ballots/${election.uuid}`,
        data: ballot ? ballot : "null",
        validateStatus(status) {
          return status < 500;
        },
      });
      if (resp.status == 200) return resp.data;
      else if (resp.status == 401)
        throw new Error("Codes invalides, veuillez réssayer.");
      else if (resp.status == 409)
        throw new Error("Vous avez déjà voté pour ce scrutin");
      else if (resp.status == 410) throw new Error("Ce scrutin est fermé");
      else if (resp.status == 429)
        throw new Error("Trop d'essais invalides, veuillez patienter.");
      else throw new Error("Veuillez vérifier que la clé saisie est la bonne");
    },
    async tally(store, { election }) {
      const resp = await api(this, {
        method: "POST",
        transformResponse: [data => data],
        url: `/elections/${election.uuid}/close`,
      });
      const tally = resp.data;
      store.commit("updateElection", {
        uuid: election.uuid,
        tally,
        tallyTime: new Date(),
      });
      return tally;
    },
    async decrypt(store, { election, trustee, partial }) {
      assert(store.state.elections);
      await api(this, {
        method: "PUT",
        url: `/elections/${election.uuid}/trustees/${trustee.email}/tally`,
        data: partial,
      });
    },
  },
};

async function make() {
  const vuex = new Vuex.Store<State>(store);
  const email = storage.getItem("email");
  const password = storage.getItem("password");
  api(vuex, { url: "/" }).then(resp =>
    vuex.commit("backendVersion", resp.data.version)
  );
  if (email && password)
    await vuex
      .dispatch("login", { email, hashedPassword: password })
      .catch(() => vuex.dispatch("logout"));
  return vuex;
}

export default make();
